import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ListTodo, Plus } from 'lucide-react'
import { createTask, listTasks, updateTaskStatus } from '../../../api/admin'
import type { VolunteerTask } from '../../../api/schemas'
import { listEvents } from '../../../api/events'
import { QueryBoundary } from '../../../components/common/QueryBoundary'
import { StatusBadge } from '../../../components/common/Status'
import { TimeAgo } from '../../../components/common/TimeAgo'
import { Skeleton } from '../../../components/ui/skeleton'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { formatCoord, formatNum } from '../../../lib/format'
import { toast } from '../../../stores/toast'

const PAGE_SIZE = 20

const TASK_STATUS_FLOW: Record<string, string[]> = {
  pending: ['assigned'],
  assigned: ['accepted', 'timeout'],
  accepted: ['done'],
  done: [],
  timeout: [],
}

const TASK_STATUS_LABEL: Record<string, string> = {
  pending: '待派发', assigned: '已派发', accepted: '已接受', done: '已完成', timeout: '超时',
}

const TASK_TYPE_LABEL: Record<string, string> = {
  rescue: '救援', transport: '转运', medical: '医疗', report: '灾情上报',
}

export function TasksSection() {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const query = useQuery({
    queryKey: ['v2', 'tasks', { status: status || undefined, page }],
    queryFn: () => listTasks({ status: status || undefined, page, pageSize: PAGE_SIZE }),
    retry: 1,
  })

  const data = query.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="panel-glass overflow-hidden rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex gap-1.5">
          {['', 'pending', 'assigned', 'accepted', 'done', 'timeout'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => {
                setStatus(s)
                setPage(1)
              }}
              className={`rounded-md px-2.5 py-1 text-[11px] transition ${
                status === s ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-foreground/[.05]'
              }`}
            >
              {s === '' ? '全部' : TASK_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> 新建任务
        </Button>
      </div>

      <QueryBoundary
        query={query}
        skeleton={<div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>}
        isEmpty={(d) => d.items.length === 0}
        empty={{ title: '暂无任务', description: '为确认事件派发应急处置任务', icon: <ListTodo size={24} /> }}
      >
        {(d) => (
          <>
            <table className="w-full text-left">
              <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">任务</th>
                  <th className="px-4 py-2.5 font-medium">类型</th>
                  <th className="px-4 py-2.5 font-medium">事件</th>
                  <th className="px-4 py-2.5 font-medium">执行人</th>
                  <th className="px-4 py-2.5 font-medium">半径</th>
                  <th className="px-4 py-2.5 font-medium">状态</th>
                  <th className="px-4 py-2.5 font-medium">创建</th>
                  <th className="px-4 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {d.items.map((task) => (
                  <TaskRow key={task.taskId} task={task} />
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              <span>共 {formatNum(d.total)} 条 · 第 {page}/{totalPages} 页</span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>上一页</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>下一页</Button>
              </div>
            </div>
          </>
        )}
      </QueryBoundary>

      <CreateTaskDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

function TaskRow({ task }: { task: VolunteerTask }) {
  const qc = useQueryClient()
  const nextStates = TASK_STATUS_FLOW[task.status] ?? []
  const mutation = useMutation({
    mutationFn: (next: string) => updateTaskStatus(task.taskId, { status: next as 'pending' | 'assigned' | 'accepted' | 'done' | 'timeout' }),
    onSuccess: () => {
      toast.success('任务状态已更新', task.taskId)
      void qc.invalidateQueries({ queryKey: ['v2', 'tasks'] })
      void qc.invalidateQueries({ queryKey: ['v2', 'audit'] })
    },
    onError: (err: Error) => toast.error('状态更新失败', err.message),
  })

  return (
    <tr className="text-xs transition hover:bg-foreground/[.03]">
      <td className="px-4 py-2.5 font-mono text-[11px] text-foreground/90">{task.taskId}</td>
      <td className="px-4 py-2.5 text-foreground/90">{TASK_TYPE_LABEL[task.type] ?? task.type}</td>
      <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{task.eventId}</td>
      <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{task.volunteerId || '未指派'}</td>
      <td className="px-4 py-2.5 font-mono tabular-nums text-muted-foreground">
        {task.radiusKm} km{task.lat || task.lon ? ` · ${formatCoord(task.lat, task.lon)}` : ''}
      </td>
      <td className="px-4 py-2.5"><StatusBadge status={task.status} labels={TASK_STATUS_LABEL} /></td>
      <td className="px-4 py-2.5"><TimeAgo ts={task.createdAt} className="text-muted-foreground" /></td>
      <td className="px-4 py-2.5">
        {nextStates.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                状态流转
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>流转到</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {nextStates.map((next) => (
                <DropdownMenuItem key={next} onSelect={() => mutation.mutate(next)}>
                  {TASK_STATUS_LABEL[next]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  )
}

function CreateTaskDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const eventsQuery = useQuery({
    queryKey: ['events', 'list', { status: 'confirmed', source: 'task-create' }],
    queryFn: () => listEvents({ status: 'confirmed', page: 1, pageSize: 50 }),
    enabled: open,
    retry: 1,
  })

  const [form, setForm] = useState({ eventId: '', type: 'report', lat: '', lon: '', radiusKm: '5', regionCode: '' })

  const mutation = useMutation({
    mutationFn: () =>
      createTask({
        eventId: form.eventId,
        type: form.type,
        lat: form.lat ? Number(form.lat) : undefined,
        lon: form.lon ? Number(form.lon) : undefined,
        radiusKm: form.radiusKm ? Number(form.radiusKm) : undefined,
        regionCode: form.regionCode.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('任务已创建')
      void qc.invalidateQueries({ queryKey: ['v2', 'tasks'] })
      void qc.invalidateQueries({ queryKey: ['v2', 'audit'] })
      setForm({ eventId: '', type: 'report', lat: '', lon: '', radiusKm: '5', regionCode: '' })
      onClose()
    },
    onError: (err: Error) => toast.error('任务创建失败', err.message),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>新建应急任务</DialogTitle>
          <DialogDescription>为已确认事件派发救援/转运/医疗/上报任务</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="task-event">关联事件（已确认）</Label>
            <select
              id="task-event"
              value={form.eventId}
              onChange={(e) => setForm((f) => ({ ...f, eventId: e.target.value }))}
              className="mt-1.5 flex h-9 w-full rounded-lg border border-input bg-black/20 px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <option value="">选择事件…</option>
              {(eventsQuery.data?.items ?? []).map((e) => (
                <option key={e.eventId} value={e.eventId}>
                  {e.name || e.eventId}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="task-type">类型</Label>
              <select
                id="task-type"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="mt-1.5 flex h-9 w-full rounded-lg border border-input bg-black/20 px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                {Object.entries(TASK_TYPE_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="task-radius">半径 (km)</Label>
              <Input id="task-radius" value={form.radiusKm} onChange={(e) => setForm((f) => ({ ...f, radiusKm: e.target.value }))} className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="task-lat">纬度（可选）</Label>
              <Input id="task-lat" value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="task-lon">经度（可选）</Label>
              <Input id="task-lon" value={form.lon} onChange={(e) => setForm((f) => ({ ...f, lon: e.target.value }))} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label htmlFor="task-region">区域代码（可选）</Label>
            <Input id="task-region" value={form.regionCode} onChange={(e) => setForm((f) => ({ ...f, regionCode: e.target.value }))} className="mt-1.5" />
          </div>
          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              disabled={mutation.isPending || !form.eventId}
              onClick={() => mutation.mutate()}
            >
              创建任务
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
